import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star } from "lucide-react";
import AxosShell from "./AxosShell";
import { Block } from "./Annotate";
import { LISTICLE_PINS } from "./tests";
import { A, SERIF, MONO, ADS } from "./brand";

const ITEMS = [
  {
    id: "list-item1",
    t: "Stacking six bottles that were never designed to work together",
    b: "Nobody plans a stack. It accumulates, one bad week at a time. The problem is that two of those bottles compete for the same absorption pathway and one of them is probably cancelling out another, and you have no way of telling which.",
    img: ADS.routine,
  },
  {
    id: null,
    t: "Taking it at the same time as levothyroxine",
    b: "Minerals and thyroid medication do not belong in the same hour. Four hours apart is the simple rule, and it is the single change that makes the rest of the routine worth doing.",
    img: null,
  },
  {
    id: "list-cta-mid",
    t: "Buying for a symptom instead of a status",
    b: "Waiting on blood work is not the same position as eight years into treatment, and it should not lead to the same bottle. Axos asks about your diagnosis before it recommends anything, which is why the match is different per person.",
    img: null,
    inline: true,
  },
  {
    id: null,
    t: "Trusting a proprietary blend",
    b: "If the label will not tell you the amount, assume the amount is not worth printing. Read the numbers off the front. Selenium at 200mcg, zinc at 15mg, vitamin D3 at 2000iu with K2, methylated B12 at 500mcg.",
    img: null,
  },
  {
    id: null,
    t: "Quitting at week two",
    b: "Nutrient status does not move in a weekend. Most reviews describe a change between week three and week six, which is why the first order is a full thirty days rather than a sample.",
    img: ADS.simplified,
  },
];

export default function AxosListicle() {
  return (
    <AxosShell pins={LISTICLE_PINS} kicker="Axos · listicle">
      <div style={{ background: A.white, color: A.body }}>
        <div className="flex items-center justify-between border-b px-5 py-3.5 sm:px-10" style={{ borderColor: A.line }}>
          <div className="text-[15px] tracking-[0.22em]" style={{ fontFamily: SERIF, color: A.navy, fontWeight: 600 }}>
            HEALTH DESK
          </div>
          <span className="rounded px-2 py-[3px] text-[10px] uppercase tracking-[0.16em]" style={{ fontFamily: MONO, color: A.soft, border: `1px solid ${A.line}`, background: A.cream }}>
            Advertisement
          </span>
        </div>

        <article className="mx-auto max-w-[720px] px-5 pb-6 pt-10 sm:px-0">
          <Block id="list-headline">
            <div className="mb-4 text-[10px] uppercase tracking-[0.18em]" style={{ fontFamily: MONO, color: A.blushDeep }}>
              Listicle · 5 items
            </div>
            <h1 className="text-4xl leading-[1.06] sm:text-5xl" style={{ fontFamily: SERIF, color: A.navy, fontWeight: 500, fontSize: 40 }}>
              5 things people with Hashimoto's get wrong about supplements
            </h1>
            <p className="mt-4 text-base leading-relaxed sm:text-lg" style={{ color: A.body }}>
              Number one is the one almost everybody is doing right now.
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-3 border-y py-3 text-[12px]" style={{ borderColor: A.line, color: A.soft }}>
              <span>By Helen Marsh</span>
              <span>·</span>
              <span>4 minute read</span>
              <span>·</span>
              <span>Paid for by Axos</span>
            </div>
          </Block>

          {ITEMS.map((it, i) => {
            const body = (
              <div className="border-b py-8" style={{ borderColor: A.line }}>
                <div className="flex items-start gap-4">
                  <span
                    className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-[14px]"
                    style={{ background: A.navy, color: A.white, fontFamily: MONO }}
                  >
                    {i + 1}
                  </span>
                  <div className="min-w-0">
                    <h2 className="text-base leading-snug md:text-lg" style={{ fontFamily: SERIF, color: A.navy, fontSize: 24, fontWeight: 500 }}>
                      {it.t}
                    </h2>
                    <p className="mt-3 text-[16px] leading-[1.75]" style={{ color: A.ink }}>
                      {it.b}
                      {it.inline && (
                        <>
                          {" "}
                          <Link to="/axos/quiz" data-testid="list-inline-cta" className="underline" style={{ color: A.blushDeep }}>
                            Answer the three questions here
                          </Link>
                          .
                        </>
                      )}
                    </p>
                    {it.img && (
                      <img
                        src={it.img}
                        alt={it.t}
                        className="mt-5 w-full rounded-[16px]"
                        style={{ border: `1px solid ${A.line}` }}
                      />
                    )}
                  </div>
                </div>
              </div>
            );
            return it.id ? (
              <Block key={i} id={it.id}>
                {body}
              </Block>
            ) : (
              <div key={i}>{body}</div>
            );
          })}

          <Block id="list-proof" className="my-10">
            <div className="rounded-[20px] p-6" style={{ background: A.cream, border: `1px solid ${A.line}` }}>
              <div className="mb-4 flex items-center gap-2 text-[12.5px]" style={{ color: A.soft }}>
                <span className="flex items-center gap-1">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star key={i} size={12} fill={A.blush} color={A.blush} />
                  ))}
                </span>
                4.7 from 1,571 reviews
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                {[
                  ["Marianne T.", "Hashimoto's, four years", "It replaced five bottles and my last panel was the best it has been."],
                  ["Priya R.", "Diagnosed 2023", "Day 28 and I am steadier through the afternoon."],
                  ["Kate D.", "Subscribed since March", "The quiz told me which one to start with. Nobody else did."],
                ].map(([n, t, q]) => (
                  <figure key={n}>
                    <blockquote className="text-[13.5px] leading-relaxed" style={{ fontFamily: SERIF, color: A.ink }}>
                      “{q}”
                    </blockquote>
                    <figcaption className="mt-2 text-[11.5px]" style={{ color: A.soft }}>
                      {n} · {t}
                    </figcaption>
                  </figure>
                ))}
              </div>
            </div>
          </Block>

          <Block id="list-end">
            <div className="rounded-[22px] p-7 text-center" style={{ background: A.navy }}>
              <div style={{ fontFamily: SERIF, color: A.white, fontSize: 26, fontWeight: 500, lineHeight: 1.2 }}>
                See which Axos routine fits you, then unlock your offer
              </div>
              <p className="mx-auto mt-3 max-w-[420px] text-[13.5px]" style={{ color: "#C3CEDC" }}>
                Three questions. About sixty seconds. No stack required.
              </p>
              <Link
                to="/axos/quiz"
                data-testid="list-end-cta"
                className="mt-6 inline-flex items-center gap-2.5 rounded-full px-7 py-3.5 text-[14.5px] font-medium"
                style={{ background: A.blush, color: A.white }}
              >
                Start the quiz
                <ArrowRight size={16} />
              </Link>
            </div>
          </Block>
        </article>
      </div>
    </AxosShell>
  );
}
